import React, {useState, useEffect, useRef, useCallback} from "react";
import "./CustomCursor.scss";

export default function CustomCursor({isDark}) {
  const [pos, setPos] = useState({x: -200, y: -200});
  const [isPointer, setIsPointer] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const rafRef = useRef(null);
  const pendingPos = useRef({x: -200, y: -200});

  const handleMouseMove = useCallback(e => {
    pendingPos.current = {x: e.clientX, y: e.clientY};
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(() => {
        setPos({...pendingPos.current});
        setIsVisible(true);
        rafRef.current = null;
      });
    }
  }, []);

  const handleMouseOver = useCallback(e => {
    const interactive = e.target.closest(
      "a, button, [role='button'], input, textarea, select, label, .project-tag, .main-button, .icon-button"
    );
    setIsPointer(!!interactive);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsVisible(false);
  }, []);

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(pointer: coarse)").matches
    ) {
      return;
    }
    window.addEventListener("mousemove", handleMouseMove, {passive: true});
    window.addEventListener("mouseover", handleMouseOver, {passive: true});
    document.documentElement.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseover", handleMouseOver);
      document.documentElement.removeEventListener(
        "mouseleave",
        handleMouseLeave
      );
    };
  }, [handleMouseMove, handleMouseOver, handleMouseLeave]);

  if (
    typeof window !== "undefined" &&
    window.matchMedia("(pointer: coarse)").matches
  ) {
    return null;
  }

  const classes = [
    "custom-cursor",
    isDark ? "custom-cursor--dark" : "custom-cursor--light",
    isPointer ? "custom-cursor--pointer" : "custom-cursor--default",
    isVisible ? "custom-cursor--visible" : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={classes}
      style={{left: pos.x, top: pos.y}}
      aria-hidden="true"
    />
  );
}
